import { useEffect, useState } from "react";
import { I } from "@/components/Icon";

/** Copy the current URL to clipboard with a brief "✓ Link copied"
 *  confirmation toast handled by the parent (`onCopied` callback fires
 *  after ~1.5s so the user gets immediate feedback without a modal.
 *
 *  `navigator.clipboard.writeText` can reject if the document isn't
 *  focused or the browser's permission prompt is denied — we fall
 *  back to a legacy `execCommand` hidden-textarea path so copy still
 *  works in those edge cases (mostly: iframes, older Safari). */
export function CopyLinkButton({ onCopied }: { onCopied: () => void }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  const copy = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // execCommand fallback — deprecated but still broadly
      // supported, and the only path that works when the tab
      // isn't focused at click time (e.g. inside a popover).
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* swallow — at worst the user gets no feedback, better than a crash */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    onCopied();
  };

  return (
    <button
      type="button"
      className="btn icon"
      onClick={() => void copy()}
      title={copied ? "Link copied" : "Copy shareable link"}
      aria-label="Copy shareable dashboard link"
    >
      {copied ? I.check : I.link}
    </button>
  );
}
